import React, { useState, useContext, useEffect, useRef } from 'react';

import { DeepCopy } from '../../tables/shared/TableEventHelpers';
import { Debouncer } from '../shared/Functions';
import { CrmRow } from '../shared/RowComponents';
import { GetWindow } from '../requests/CommonRequests';
import { CalculateSettings } from '../shared/Functions';
import { DataContext as DealDataContext } from '../../../stores/crm-deal-store';
import { applyFilterTable } from '../deal/DealFilterHelpers';

import LoadingSpacer from './LoadingSpacer';
import NoDataCrm from './NoDataCrm';

// Reference: https://blog.logrocket.com/virtual-scrolling-core-principles-and-basic-implementation-in-react/
const VirtualScroller = ({ dataContext, onCellClick, isDoingInitialFetch }) => {
    const {
        store: { apiUrl, layout, requestObject, viewSettings, rows },
        dispatch,
    } = useContext(dataContext);
    const {
        store: { userFilterType, selectedUsersOptions, selectedUserGroupOption, selectedBossGroupOption, selectedTeamGroupOption },
    } = useContext(DealDataContext);

    const handleUpdateUsers = (filterKey, users) => {
        let newFilterUsers = [];
        newFilterUsers = users.map((item) => {
            if (item == null) {
                return null;
            } else {
                return {
                    id: item.id,
                    name: item.name,
                };
            }
        });
        const newSerializedFilterUsers = JSON.stringify(newFilterUsers);
        //requestObject.filters['Users'] = newSerializedFilterUsers;
        //requestObject.filters["GroupType"] = filterKey;
        applyFilterTable(filterKey, newSerializedFilterUsers, dispatch, requestObject, viewSettings);
        //onUserFilterChange(newSerializedFilterUsers);
        //applyFilter(filterKey, newSerializedFilterUsers, apiUrl, activeDispatch, requestObject, viewSettings);
    };

    const [oldIndex, setOldIndex] = useState(0);
    const [index, setIndex] = useState(requestObject.startIndex);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const debouncedIndex = Debouncer(index, 200);
    const previousFilter = useRef(requestObject.filters);

    const finishedWindowGet = (res) => {
        if (res.success) {
            let newViewSettings = DeepCopy(viewSettings);
            newViewSettings.maxIndex = res.data.totalCount;
            newViewSettings.startIndex = debouncedIndex;
            newViewSettings = CalculateSettings(newViewSettings, requestObject);
            dispatch({ type: 'UPDATE_DATA', payload: res.data.dataEntities });
            dispatch({ type: 'UPDATE_VIEW_SETTINGS', viewSettings: newViewSettings });
        }
        setIsLoadingData(false);
    };

    const applyFilters = () => {
        localStorage.setItem(`deal-user-filter-type`, JSON.stringify(userFilterType));
        switch (userFilterType.id) {
            case 'user':
                localStorage.setItem(`deal-selected-users-options`, JSON.stringify(selectedUsersOptions ?? []));
                handleUpdateUsers('Users', selectedUsersOptions ?? []);
                break;
            case 'boss':
                localStorage.setItem(`deal-selected-boss-group-option`, JSON.stringify(selectedBossGroupOption ?? []));
                handleUpdateUsers('Boss', [selectedBossGroupOption] ?? []);
                break;
            case 'group':
                localStorage.setItem(`deal-selected-user-group-option`, JSON.stringify(selectedUserGroupOption ?? []));
                handleUpdateUsers('Group', [selectedUserGroupOption] ?? []);
                break;
            case 'team':
                localStorage.setItem(`deal-selected-team-group-option`, JSON.stringify(selectedTeamGroupOption ?? []));
                handleUpdateUsers('Users', selectedTeamGroupOption?.users ?? []);
                break;
            default:
                break;
        }
    }

    useEffect(() => {
        applyFilters()
    }, [userFilterType, selectedUsersOptions, selectedUserGroupOption, selectedBossGroupOption, selectedTeamGroupOption]);


    const handleVirtualScroll = (e) => {
        let scrollTop = e.target.scrollingElement.scrollTop - 140;
        let minIndex = viewSettings.minIndex;
        let toleranceHeight = viewSettings.tolerance * viewSettings.itemHeight;
        let itemHeight = viewSettings.itemHeight;
        let tolerance = viewSettings.tolerance;

        setIndex(minIndex + Math.floor(((scrollTop >= 0 ? scrollTop : 0) - toleranceHeight) / itemHeight) + tolerance);
    };


    useEffect(() => {
        const timeout = setTimeout(() => {
        if (rows.length === 0 && !isLoadingData) {
            setIsLoadingData(true);
            GetWindow(apiUrl, requestObject, finishedWindowGet);
        } else {
            // If filter has been changed
            previousFilter.current = requestObject.filters;
            GetWindow(apiUrl, requestObject, finishedWindowGet);
        }
    }, 100);
    return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestObject]);

    useEffect(() => {
        let stickyElement = document.querySelector('.sticky-element');

        if (stickyElement !== undefined && stickyElement !== null) {
            let parent = stickyElement.parentElement;

            while (parent) {
                const hasOverflow = getComputedStyle(parent).overflow;
                if (hasOverflow !== 'visible') parent.overflow = 'visible';

                parent = parent.parentElement;
            }
        }
    }, []);

    useEffect(() => {
        function watchScroll() {
            window.addEventListener('scroll', handleVirtualScroll);
        }

        watchScroll();
        return () => {
            window.removeEventListener('scroll', handleVirtualScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    useEffect(() => {
        if (debouncedIndex !== oldIndex) {
            setOldIndex(debouncedIndex);
            const len = requestObject.amount + 2 * viewSettings.tolerance;
            let newRequestObject = DeepCopy(requestObject);
            newRequestObject.startIndex = debouncedIndex;
            const topPaddingHeight = Math.min(
                Math.max((debouncedIndex - (viewSettings.tolerance + 1)) * viewSettings.itemHeight, 0),
                viewSettings.totalHeight
            );

            const bottomPaddingHeight = Math.max(viewSettings.totalHeight - topPaddingHeight - len * viewSettings.itemHeight, 0);

            let newViewSettings = DeepCopy(viewSettings);

            viewSettings.topPaddingHeight = topPaddingHeight;
            viewSettings.bottomPaddingHeight = bottomPaddingHeight;

            // Trigger update of RequestObject, which triggers a new load
            dispatch({ type: 'UPDATE_VIEW_SETTINGS_AND_REQUEST_OBJECT', viewSettings: newViewSettings, requestObject: newRequestObject });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedIndex]);

    if (isLoadingData && !rows?.length) {
        return <LoadingSpacer isLoading={isLoadingData} height={800} />;
    } else {
        return (
            <div className='viewport d-flex flex-column justify-content-center align-items-center w-100'>
                <LoadingSpacer isLoading={isLoadingData} height={viewSettings.topPaddingHeight} shouldFlushBottom={true} />
                {rows &&
                    rows.map((row) => {
                        return (
                            <CrmRow key={row.id} layout={layout} height={viewSettings.itemHeight} item={row} handleOnClick={onCellClick} disabled={row.deal === null ? true: false} />
                        );
                    })}
                <LoadingSpacer isLoading={isLoadingData} height={viewSettings.bottomPaddingHeight} shouldFlushBottom={false} />
                {isDoingInitialFetch ? null : <NoDataCrm rows={rows} />}
            </div>
        );
    }
};

export default VirtualScroller;
